import { formatDate } from './format';
import { formatEstimate } from './estimate';
import { translate } from './i18n';

/**
 * The task audit trail in words: what changed, from what, to what.
 *
 * Rows carry the raw values (`oldValue`/`newValue`: ids, slugs, ISO dates) and,
 * for anything that is an id, a readable snapshot (`oldDisplay`/`newDisplay`)
 * taken when the change happened — so a removed label or a deleted comment
 * still says what it was. Shared by the task timeline and the person feed.
 */
export interface ActivityLike {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  oldDisplay?: string | null;
  newDisplay?: string | null;
}

export interface ActivityDescription {
  /** The sentence after the actor's name, e.g. "a setat termenul la 20 sept. 2026". */
  text: string;
  /**
   * Long values (comment text, description) that don't fit in a sentence;
   * shown underneath. `before` alone means removed, `after` alone means added.
   */
  detail?: { before?: string | null; after?: string | null };
}

const RELATION: Record<string, string> = {
  BLOCKS: 'blochează',
  BLOCKED_BY: 'este blocată de',
  RELATED: 'este legată de',
  DUPLICATE: 'este duplicat al',
};

function prettyEnum(v: string): string {
  return v
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const q = (s: string) => `„${s}”`;

/**
 * Whether an audit row says nothing the comment list above it doesn't already
 * show, and so belongs only there rather than in the change history.
 *
 * Only posting counts. A posted comment appears in full in the comment
 * section, so "X a adăugat un comentariu" with the same text quoted under it
 * is the same content twice, and on a busy ticket those copies bury the edits
 * someone actually came to find — status, assignee, estimate, due date.
 *
 * Edits and deletions stay in the history on purpose. The comment list shows
 * only a comment's current text, and a deleted comment is not there at all,
 * so for both of those the audit row is the only surviving record of what
 * happened.
 *
 * Deliberately narrow: every other field is a change to the task, including
 * any added later, so a new property starts out visible in the history rather
 * than silently missing from it.
 */
export function isShownByCommentList(a: Pick<ActivityLike, 'field'>): boolean {
  return a.field === 'comment_added';
}

/** Pick the sentence for a value that was set, changed or cleared. */
function change(
  oldV: string | null,
  newV: string | null,
  set: (v: string) => string,
  changed: (from: string, to: string) => string,
  cleared: (was: string | null) => string,
): string {
  if (newV && oldV) return changed(oldV, newV);
  if (newV) return set(newV);
  return cleared(oldV);
}

function dueDate(v: string | null): string | null {
  return v ? formatDate(v) : null;
}

function estimate(v: string | null): string | null {
  return v == null || v === '' ? null : formatEstimate(Number(v));
}

export function describeActivity(a: ActivityLike): ActivityDescription {
  const oldD = a.oldDisplay ?? null;
  const newD = a.newDisplay ?? null;

  switch (a.field) {
    case 'created':
      return { text: 'a creat sarcina' };

    case 'comment_added':
      return newD
        ? { text: 'a adăugat un comentariu', detail: { after: newD } }
        : { text: 'a comentat' };
    case 'comment_updated':
      return { text: 'a editat un comentariu', detail: { before: oldD, after: newD } };
    case 'comment_deleted':
      return oldD
        ? { text: 'a șters un comentariu', detail: { before: oldD } }
        : { text: 'a șters un comentariu' };

    case 'label_added':
      return { text: newD ? `a adăugat eticheta ${q(newD)}` : 'a adăugat o etichetă' };
    case 'label_removed':
      return { text: oldD ? `a eliminat eticheta ${q(oldD)}` : 'a eliminat o etichetă' };

    case 'status': {
      const from = oldD ?? (a.oldValue ? prettyEnum(a.oldValue) : null);
      const to = newD ?? (a.newValue ? prettyEnum(a.newValue) : null);
      if (from && to) return { text: `a schimbat statusul din ${from} în ${to}` };
      return { text: to ? `a schimbat statusul în ${to}` : 'a schimbat statusul' };
    }

    case 'priority': {
      const p = (v: string | null) => (v ? translate(`tasks.priority.${v}`) : null);
      const from = p(a.oldValue);
      const to = p(a.newValue);
      if (from && to) return { text: `a schimbat prioritatea din ${from} în ${to}` };
      return { text: to ? `a setat prioritatea la ${to}` : 'a schimbat prioritatea' };
    }

    case 'assigneeId':
      return {
        text: change(
          oldD ?? (a.oldValue ? 'altcineva' : null),
          newD ?? (a.newValue ? 'altcineva' : null),
          (to) => `a atribuit sarcina lui ${to}`,
          (from, to) => `a schimbat responsabilul din ${from} în ${to}`,
          (was) => (was ? `a eliminat responsabilul ${was}` : 'a eliminat responsabilul'),
        ),
      };

    case 'transferred':
      return { text: newD ? `a transferat sarcina către ${newD}` : 'a transferat sarcina' };

    case 'dueDate':
      return {
        text: change(
          dueDate(a.oldValue),
          dueDate(a.newValue),
          (to) => `a setat termenul la ${to}`,
          (from, to) => `a schimbat termenul din ${from} în ${to}`,
          (was) => (was ? `a șters termenul (era ${was})` : 'a șters termenul'),
        ),
      };

    case 'estimate':
      return {
        text: change(
          estimate(a.oldValue),
          estimate(a.newValue),
          (to) => `a setat estimarea la ${to}`,
          (from, to) => `a schimbat estimarea din ${from} în ${to}`,
          (was) => (was ? `a șters estimarea (era ${was})` : 'a șters estimarea'),
        ),
      };

    case 'identifier':
      // The tag changed because the task moved to another project. Worth its
      // own sentence: someone holding a link to the old one needs to see why
      // it now points at a different number.
      return {
        text:
          a.oldValue && a.newValue
            ? `a mutat sarcina și a renumerotat-o din ${a.oldValue} în ${a.newValue}`
            : `a renumerotat sarcina în ${a.newValue ?? '—'}`,
      };

    case 'title':
      return a.oldValue && a.newValue
        ? { text: `a redenumit sarcina din ${q(a.oldValue)} în ${q(a.newValue)}` }
        : { text: a.newValue ? `a redenumit sarcina în ${q(a.newValue)}` : 'a redenumit sarcina' };

    case 'description': {
      const before = a.oldValue?.trim() || null;
      const after = a.newValue?.trim() || null;
      if (!before && after) return { text: 'a adăugat descrierea', detail: { after } };
      if (before && !after) return { text: 'a șters descrierea', detail: { before } };
      return { text: 'a actualizat descrierea', detail: { before, after } };
    }

    case 'projectId':
      return {
        text:
          oldD && newD
            ? `a mutat sarcina din proiectul ${q(oldD)} în ${q(newD)}`
            : newD
              ? `a mutat sarcina în proiectul ${q(newD)}`
              : 'a mutat sarcina în alt proiect',
      };

    case 'parentId':
      return {
        text: change(
          oldD ?? (a.oldValue ? 'altă sarcină' : null),
          newD ?? (a.newValue ? 'altă sarcină' : null),
          (to) => `a setat sarcina părinte la ${to}`,
          (from, to) => `a schimbat sarcina părinte din ${from} în ${to}`,
          (was) => (was ? `a eliminat sarcina părinte ${was}` : 'a eliminat sarcina părinte'),
        ),
      };

    case 'departmentId':
      return {
        text: change(
          oldD ?? (a.oldValue ? 'alt departament' : null),
          newD ?? (a.newValue ? 'alt departament' : null),
          (to) => `a setat departamentul la ${q(to)}`,
          (from, to) => `a schimbat departamentul din ${q(from)} în ${q(to)}`,
          (was) => (was ? `a eliminat departamentul ${q(was)}` : 'a eliminat departamentul'),
        ),
      };

    case 'serviceId':
      return {
        text: change(
          oldD ?? (a.oldValue ? 'alt serviciu' : null),
          newD ?? (a.newValue ? 'alt serviciu' : null),
          (to) => `a setat serviciul la ${q(to)}`,
          (from, to) => `a schimbat serviciul din ${q(from)} în ${q(to)}`,
          (was) => (was ? `a eliminat serviciul ${q(was)}` : 'a eliminat serviciul'),
        ),
      };

    case 'relation_added': {
      const verb = RELATION[a.newValue ?? ''] ?? 'este legată de';
      return { text: `a marcat că sarcina ${verb} ${newD ?? 'altă sarcină'}` };
    }
    case 'relation_removed': {
      const verb = RELATION[a.oldValue ?? ''] ?? 'este legată de';
      return { text: `a scos legătura: sarcina ${verb} ${oldD ?? 'altă sarcină'}` };
    }

    case 'time_logged':
      // newValue is decimal hours, from both the manual worklog and the timer's Stop.
      return { text: `a înregistrat ${formatEstimate(Number(a.newValue))} lucrate` };
    case 'time_updated':
      return {
        text: `a corectat timpul lucrat de la ${formatEstimate(
          Number(a.oldValue),
        )} la ${formatEstimate(Number(a.newValue))}`,
      };
    case 'time_deleted':
      // The hours are gone from every total, so the feed is the only place left
      // that says they were ever there.
      return { text: `a șters ${formatEstimate(Number(a.oldValue))} lucrate` };

    case 'daily_check_in':
      return {
        text: a.newValue
          ? `a raportat statusul zilnic: ${translate(`dailyStatus.status.${a.newValue}`)}`
          : 'a raportat statusul zilnic',
      };

    default: {
      const name = a.field.replace(/([A-Z])/g, ' $1').toLowerCase();
      const from = oldD ?? a.oldValue;
      const to = newD ?? a.newValue;
      if (from && to) return { text: `a schimbat ${name} din ${q(from)} în ${q(to)}` };
      if (to) return { text: `a setat ${name} la ${q(to)}` };
      return { text: from ? `a șters ${name} (era ${q(from)})` : `a actualizat ${name}` };
    }
  }
}
